import { FormEvent, useEffect, useState } from 'react';
import ParentPortalShell from '@/components/parent/ParentPortalShell';
import { ParentProfile, clubsBookingApi } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';

type EditableChild = {
  id?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
};

const ParentProfilePage = () => {
  const [profile, setProfile] = useState<ParentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    children: [{ firstName: '', lastName: '', dateOfBirth: '' }] as EditableChild[],
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const token = auth.getToken();
        if (!token) {
          setError('Your parent session has expired. Please log in again.');
          return;
        }
        clubsBookingApi.setAuthToken(token);
        const data = await clubsBookingApi.getParentProfile();
        setProfile(data);
        setForm({
          name: data.name || '',
          phone: data.phone || '',
          children: data.children?.length
            ? data.children.map((child) => ({
                id: child.id,
                firstName: child.firstName || '',
                lastName: child.lastName || '',
                dateOfBirth: child.dateOfBirth || '',
              }))
            : [{ firstName: '', lastName: '', dateOfBirth: '' }],
        });
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          auth.clearAuth();
          clubsBookingApi.clearAuthToken();
          window.location.assign('/parent/login');
          return;
        }
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load your profile');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const token = auth.getToken();
      if (!token) {
        setError('Your parent session has expired. Please log in again.');
        return;
      }
      clubsBookingApi.setAuthToken(token);
      const sanitizedChildren = form.children
        .map((child) => ({
          id: child.id,
          firstName: child.firstName.trim(),
          lastName: child.lastName.trim(),
          dateOfBirth: child.dateOfBirth,
        }))
        .filter((child) => child.firstName || child.lastName || child.dateOfBirth);

      if (sanitizedChildren.some((child) => !child.firstName || !child.lastName || !child.dateOfBirth)) {
        setError('Each child needs a first name, last name, and date of birth.');
        setSaving(false);
        return;
      }

      const next = await clubsBookingApi.updateParentProfile({
        name: form.name,
        phone: form.phone || undefined,
        children: sanitizedChildren,
      });
      setProfile(next);
      setForm((prev) => ({
        ...prev,
        children: next.children?.length
          ? next.children.map((child) => ({
              id: child.id,
              firstName: child.firstName || '',
              lastName: child.lastName || '',
              dateOfBirth: child.dateOfBirth || '',
            }))
          : [{ firstName: '', lastName: '', dateOfBirth: '' }],
      }));
      setMessage('Profile saved.');
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ParentPortalShell title="My profile" subtitle="Saved details are auto-filled at checkout">
      <div className="w-full">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
          {loading && <p className="mt-4 text-sm text-slate-500">Loading profile...</p>}
          {error && <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {message && <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{message}</p>}

          {!loading && (
            <form onSubmit={onSubmit} className="mt-5 space-y-4">
              <label className="block text-sm text-slate-600">
                Parent name
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </label>

              <label className="block text-sm text-slate-600">
                Parent email
                <input className="mt-1 w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-slate-500" value={profile?.email || ''} disabled />
              </label>

              <label className="block text-sm text-slate-600">
                Phone number (optional)
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </label>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-700">Children</p>
                <div className="mt-3 space-y-2">
                  {form.children.map((child, index) => (
                    <div key={child.id || index} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_180px_auto]">
                      <input
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        placeholder="First name"
                        value={child.firstName}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            children: prev.children.map((entry, childIndex) =>
                              childIndex === index ? { ...entry, firstName: e.target.value } : entry,
                            ),
                          }))
                        }
                      />
                      <input
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        placeholder="Last name"
                        value={child.lastName}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            children: prev.children.map((entry, childIndex) =>
                              childIndex === index ? { ...entry, lastName: e.target.value } : entry,
                            ),
                          }))
                        }
                      />
                      <input
                        type="date"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        value={child.dateOfBirth}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            children: prev.children.map((entry, childIndex) =>
                              childIndex === index ? { ...entry, dateOfBirth: e.target.value } : entry,
                            ),
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50"
                        disabled={form.children.length === 1}
                        onClick={() => setForm((prev) => ({ ...prev, children: prev.children.filter((_, childIndex) => childIndex !== index) }))}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-3 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs hover:bg-slate-100"
                  onClick={() => setForm((prev) => ({ ...prev, children: [...prev.children, { firstName: '', lastName: '', dateOfBirth: '' }] }))}
                >
                  Add child
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-[#1F8F5F] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#1F8F5F] disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Save profile'}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </ParentPortalShell>
  );
};

export default ParentProfilePage;


